/**
 * ehr#708 (DISCHARGE_SUMMARY_NOTE_10-30-V6, Jay's answers 10-30): the Discharge summary note's
 * own parts in the editor. Pure, so the note, its confirm and the tests read one list.
 */
import {
  DISCHARGE_KIND,
  type DischargeAppointment,
  type DischargePreview,
  type SupervisionView,
} from '@ehr/shared';

/** V6 set_phrases: the discharge summary's phrase pills, by box (V5 answer 11a). */
export const DISCHARGE_PHRASES: Record<string, readonly string[]> = {
  subjective: [
    'Client completed treatment goals.',
    'Client chose to end services.',
    'Client stopped attending after __.',
    'Client is transferring care to __.',
  ],
  assessment: [
    'Over __ sessions, client made __ progress toward treatment goals.',
    'Symptoms went from __ to __.',
    'Client learned and uses __.',
  ],
  plan: [
    'Referred to __ for ongoing care.',
    'Client was given crisis line numbers and local resources.',
    'Client can return to services any time.',
  ],
};

/** The pills under a discharge summary's box (none for a box it doesn't name). */
export const dischargePills = (key: string): readonly string[] => DISCHARGE_PHRASES[key] ?? [];

/** Jay 10-30 Q6 (and V6): the side cards a discharge summary leaves out. */
export const DISCHARGE_HIDDEN_CARDS = ['quick', 'measures', 'changes'] as const;
export type DischargeHiddenCard = (typeof DISCHARGE_HIDDEN_CARDS)[number];
/** Whether this note leaves out that side card (only a discharge summary leaves any out). */
export const cardOffForKind = (kind: string, card: DischargeHiddenCard): boolean =>
  kind === DISCHARGE_KIND && (DISCHARGE_HIDDEN_CARDS as readonly string[]).includes(card);

/**
 * Q5: the Supervision a note follows. A discharge summary goes by Co-sign discharge summaries
 * (the API's `discharge`); every other note, and an older API without it, by the setting itself.
 */
export function supervisionFor(view: SupervisionView | null, kind: string): SupervisionView | null {
  if (!view || kind !== DISCHARGE_KIND || !view.discharge) return view;
  return { ...view, ...view.discharge };
}

/**
 * V5 (bad Start time, End time or Date): an entry that can't be read goes back to the saved value,
 * and this says how to type it.
 */
export function notSavedMessage(field: 'Start time' | 'End time' | 'Date', h24 = false): string {
  if (field === 'Date') return 'Date not saved. Type a date like 10/30/2026.';
  return `${field} not saved. Type a time like ${h24 ? '15:00' : '3:00 PM'}.`;
}

/** A date and time as the confirm shows it ("Tue, Nov 3 at 3:00 PM"). */
const when = (iso: string) => {
  const d = new Date(iso);
  const day = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const t = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${day} at ${t}`;
};

/**
 * Q2: the "No treatment plan on file" reason the note saves: only while it has no plan to give
 * statuses to (no linked plan, no goal rows), so linking a plan later drops a reason picked before.
 */
export function noPlanReasonToSave(o: {
  reason: string;
  linkedPlanId: string | null;
  goalRows: number;
}): string | undefined {
  return o.reason.trim() && !o.linkedPlanId && o.goalRows === 0 ? o.reason : undefined;
}

/** Q2: the printed note's line for a discharge summary signed with no plan on file (or null). */
export function noPlanPrintLine(body: { noPlanReason?: unknown }): string | null {
  const r = typeof body.noPlanReason === 'string' ? body.noPlanReason.trim() : '';
  return r ? `No treatment plan on file: ${r}` : null;
}

/** How many appointments the confirm names one by one before "and N more". */
export const CONFIRM_APPTS_SHOWN = 5;

/**
 * One future booking as the confirm names it: a repeating one is one line for every date from its
 * next one on (however far it runs), a shared or group session says only this client comes off.
 */
export function bookingLine(a: DischargeAppointment): string {
  const at = when(a.startsAt);
  if (a.kind === 'group')
    return a.repeats ? `Group sessions, every date from ${at}` : `Group session on ${at}`;
  if (a.kind === 'added')
    return a.repeats
      ? `Repeating shared session, every date from ${at} (only this client comes off)`
      : `Shared session on ${at} (only this client comes off)`;
  return a.repeats ? `Repeating appointment, every date from ${at}` : at;
}

/**
 * Q4: the confirm's list, in plain words: exactly what signing (or co-signing) will do. With the
 * note going to a supervisor first, the same list says it happens once they co-sign. Until the
 * Calendar can cancel them (`calendarCancels`), the appointments are listed for staff to cancel.
 */
export function dischargeConfirmLines(p: DischargePreview): {
  intro: string;
  lines: string[];
  appointments: string[];
  more: number;
} {
  const lines: string[] = [];
  if (!p.stage.available)
    lines.push('No Discharged stage is set up in Settings, so the client stage stays as it is.');
  else if (p.stage.already) lines.push('The client is already Discharged.');
  else lines.push(`Set the client to Discharged${p.stage.from ? ` (now ${p.stage.from})` : ''}.`);
  if (p.plan) lines.push(`Close the treatment plan "${p.plan.label}" (it moves to past plans).`);
  else if (p.planUnknown)
    lines.push(
      'Close the current treatment plan, if there is one (the Treatment Plan app did not answer).',
    );
  else lines.push('No current treatment plan to close.');
  const n = p.appointments.length;
  if (!n) lines.push('No future appointments to cancel.');
  else if (p.calendarCancels) lines.push('Cancel their future appointments on the Calendar:');
  else
    lines.push(
      'Leave their future appointments on the Calendar (the EHR cannot cancel them there yet). Cancel these in the Calendar:',
    );
  const appointments = p.appointments.slice(0, CONFIRM_APPTS_SHOWN).map(bookingLine);
  const intro = p.waitsForCosign
    ? `This goes to ${p.cosignerName || 'your supervisor'} to co-sign first. Once they co-sign, the EHR will:`
    : 'Signing this discharge summary will:';
  return { intro, lines, appointments, more: Math.max(0, n - CONFIRM_APPTS_SHOWN) };
}
